import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { setUnauthorizedHandler, tokenStore } from '../api/client'
import { useCurrentUser } from '../api/hooks'
import type { Organization, User } from '../api/types'

interface AuthState {
  user: User | null
  organization: Organization | null
  /** True until the stored token has been checked against the API. */
  loading: boolean
  signIn: (token: string) => void
  signOut: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [token, setToken] = useState<string | null>(() => tokenStore.get())

  const signOut = useCallback(() => {
    tokenStore.clear()
    setToken(null)
    // Drop every cached response: the next user must not see the previous
    // one's projects for even a frame.
    queryClient.clear()
  }, [queryClient])

  const signIn = useCallback(
    (newToken: string) => {
      tokenStore.set(newToken)
      setToken(newToken)
      queryClient.clear()
    },
    [queryClient],
  )

  // The API client calls this when a request comes back 401, so an expired
  // token logs the user out instead of leaving a half-broken page.
  useEffect(() => {
    setUnauthorizedHandler(() => setToken(null))
    return () => setUnauthorizedHandler(null)
  }, [])

  const { data, isLoading, isError } = useCurrentUser(Boolean(token))

  useEffect(() => {
    if (isError) {
      tokenStore.clear()
      setToken(null)
    }
  }, [isError])

  const value = useMemo<AuthState>(
    () => ({
      user: data?.user ?? null,
      organization: data?.organization ?? null,
      loading: Boolean(token) && isLoading,
      signIn,
      signOut,
    }),
    [data, token, isLoading, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider')
  }
  return context
}
